import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import api from '../config/axios';
import toast from 'react-hot-toast';
import { EyeIcon, EyeSlashIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import Logo from '../components/Logo';

const AcceptInvite = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { register, handleSubmit, watch, formState: { errors } } = useForm();
  const password = watch('password');

  useEffect(() => {
    const fetchInvite = async () => {
      try {
        const response = await api.get(`/team/invite-info/${token}`);
        setInvitation(response.data.invitation);
      } catch (err) {
        setError(err.response?.data?.error || 'Invalid or expired invitation');
      } finally {
        setLoading(false);
      }
    };

    if (token) fetchInvite();
  }, [token]);

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      await api.post(`/team/accept-invite/${token}`, {
        firstName: data.firstName,
        lastName: data.lastName,
        password: data.password,
      });
      setSuccess(true);
      toast.success('Invitation accepted! You can now log in.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to accept invitation');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-100 flex items-center justify-center">
        <div className="spinner spinner-lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-neutral-100 flex items-center justify-center px-4">
        <div className="w-full max-w-md space-y-6">
          <div className="flex justify-center">
            <Logo className="h-10" showText={true} variant="dark" />
          </div>
          <div className="bg-white rounded-xl shadow-lg p-8 border border-neutral-200 text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
                <XCircleIcon className="w-8 h-8 text-red-500" />
              </div>
            </div>
            <h2 className="text-xl font-bold text-neutral-800">Invalid Invitation</h2>
            <p className="mt-2 text-neutral-500">{error}</p>
            <Link to="/login" className="btn-primary w-full mt-6 inline-block">
              Go to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-neutral-100 flex items-center justify-center px-4">
        <div className="w-full max-w-md space-y-6">
          <div className="flex justify-center">
            <Logo className="h-10" showText={true} variant="dark" />
          </div>
          <div className="bg-white rounded-xl shadow-lg p-8 border border-neutral-200 text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircleIcon className="w-8 h-8 text-green-500" />
              </div>
            </div>
            <h2 className="text-xl font-bold text-neutral-800">Welcome to the team!</h2>
            <p className="mt-2 text-neutral-500">
              Your account has been created. You can now sign in.
            </p>
            <button onClick={() => navigate('/login')} className="btn-primary w-full mt-6">
              Sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-100 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md space-y-6">
        <div className="flex justify-center">
          <Logo className="h-10" showText={true} variant="dark" />
        </div>

        <div className="text-center">
          <h2 className="text-2xl font-bold text-neutral-800">Join {invitation.tenantName}</h2>
          <p className="mt-1 text-neutral-500 text-sm">
            You've been invited as a <span className="font-medium text-primary-600">{invitation.role}</span> to join the team.
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6 border border-neutral-200">
          <div className="mb-5 p-3 bg-primary-50 rounded-lg text-center">
            <p className="text-sm text-neutral-600">
              Invitation for <span className="font-medium text-neutral-800">{invitation.email}</span>
            </p>
          </div>

          <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="input-label">First Name</label>
                <input
                  {...register('firstName', { required: 'Required', minLength: { value: 2, message: 'Min 2 chars' } })}
                  type="text"
                  className={`input-field ${errors.firstName ? 'input-error' : ''}`}
                  placeholder="John"
                />
                {errors.firstName && <p className="input-error-message">{errors.firstName.message}</p>}
              </div>
              <div>
                <label className="input-label">Last Name</label>
                <input
                  {...register('lastName', { required: 'Required', minLength: { value: 2, message: 'Min 2 chars' } })}
                  type="text"
                  className={`input-field ${errors.lastName ? 'input-error' : ''}`}
                  placeholder="Doe"
                />
                {errors.lastName && <p className="input-error-message">{errors.lastName.message}</p>}
              </div>
            </div>

            <div>
              <label className="input-label">Password</label>
              <div className="relative">
                <input
                  {...register('password', { required: 'Password is required', minLength: { value: 8, message: 'Min 8 characters' } })}
                  type={showPassword ? 'text' : 'password'}
                  className={`input-field pr-12 ${errors.password ? 'input-error' : ''}`}
                  placeholder="Create a password"
                />
                <button type="button" className="absolute inset-y-0 right-0 pr-4 flex items-center text-neutral-400 hover:text-neutral-600" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                </button>
              </div>
              {errors.password && <p className="input-error-message">{errors.password.message}</p>}
            </div>

            <div>
              <label className="input-label">Confirm Password</label>
              <div className="relative">
                <input
                  {...register('confirmPassword', { required: 'Please confirm', validate: v => v === password || 'Passwords do not match' })}
                  type={showConfirmPassword ? 'text' : 'password'}
                  className={`input-field pr-12 ${errors.confirmPassword ? 'input-error' : ''}`}
                  placeholder="Confirm password"
                />
                <button type="button" className="absolute inset-y-0 right-0 pr-4 flex items-center text-neutral-400 hover:text-neutral-600" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                  {showConfirmPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                </button>
              </div>
              {errors.confirmPassword && <p className="input-error-message">{errors.confirmPassword.message}</p>}
            </div>

            <button type="submit" disabled={isSubmitting} className="btn-primary w-full">
              {isSubmitting ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="spinner spinner-sm border-white/30 border-t-white" />
                  <span>Joining team...</span>
                </div>
              ) : (
                'Accept Invitation & Join'
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-neutral-400">
          Already have an account?{' '}
          <Link to="/login" className="text-primary-500 hover:text-primary-600 font-medium">Sign in</Link>
        </p>
      </div>
    </div>
  );
};

export default AcceptInvite;
